import { useState, useEffect, useRef } from 'react'

interface TimerOptions {
  initialTimeMinutes?: number
  onTimeUp?: () => void
  autoStart?: boolean
}

export function useInterviewTimer({ 
  initialTimeMinutes = 60, 
  onTimeUp,
  autoStart = true 
}: TimerOptions = {}) {
  const [timeRemaining, setTimeRemaining] = useState(initialTimeMinutes * 60) // Convert to seconds
  const [isRunning, setIsRunning] = useState(autoStart)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isRunning && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsRunning(false)
            onTimeUp?.()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isRunning, timeRemaining, onTimeUp])

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    }
  }

  const getTimeColor = () => {
    const minutes = timeRemaining / 60
    if (minutes <= 5) return 'text-red-600'
    if (minutes <= 10) return 'text-orange-600'
    if (minutes <= 15) return 'text-yellow-600'
    return 'text-green-600'
  }

  const getTimeStatus = () => {
    const minutes = timeRemaining / 60
    if (timeRemaining === 0) return 'Time\'s up!'
    if (minutes <= 5) return 'Urgent'
    if (minutes <= 10) return 'Warning'
    if (minutes <= 15) return 'Caution'
    return 'Good'
  }

  const startTimer = () => setIsRunning(true)
  const pauseTimer = () => setIsRunning(false)
  const resetTimer = (newTimeMinutes?: number) => {
    setTimeRemaining((newTimeMinutes || initialTimeMinutes) * 60)
    setIsRunning(autoStart)
  }

  return {
    timeRemaining,
    formattedTime: formatTime(timeRemaining),
    timeColor: getTimeColor(),
    timeStatus: getTimeStatus(),
    isRunning,
    startTimer,
    pauseTimer,
    resetTimer,
    isTimeUp: timeRemaining === 0
  }
}
